import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Handshake, X, Search, AlertCircle } from "lucide-react";
import { formatApiError } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const SEARCH_DEBOUNCE = 280;
const MIN_MESSAGE = 30;

function MemberPicker({ label, selected, onSelect, exclude, testIdPrefix }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (selected) return; // don't search once selected
    clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(
          `${API}/api/admin/profiles/search?q=${encodeURIComponent(query.trim())}`,
          { withCredentials: true }
        );
        const filtered = (data.items || []).filter((it) => it.user_id !== exclude);
        setResults(filtered);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, SEARCH_DEBOUNCE);
    return () => clearTimeout(debounceRef.current);
  }, [query, exclude, selected]);

  // Click outside
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (item) => {
    onSelect(item);
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  const clear = () => {
    onSelect(null);
    setQuery("");
  };

  return (
    <div ref={containerRef} className="relative">
      <label className="cbm-label block mb-1.5">{label}</label>
      {selected ? (
        <div
          data-testid={`${testIdPrefix}-selected`}
          className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg"
          style={{ background: "rgba(201,162,39,0.08)", border: "1px solid rgba(201,162,39,0.3)" }}
        >
          <div className="min-w-0">
            <p className="text-white text-sm font-semibold truncate">{selected.nom_prenom}</p>
            <p className="text-cbm-muted text-xs truncate">
              {selected.entreprise || "—"}{selected.email ? ` · ${selected.email}` : ""}
            </p>
          </div>
          <button
            type="button"
            data-testid={`${testIdPrefix}-clear`}
            onClick={clear}
            className="text-cbm-muted hover:text-white p-1"
            aria-label="Retirer la sélection"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-cbm-muted" />
            <input
              data-testid={`${testIdPrefix}-input`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Recherche par nom, prénom, entreprise ou email…"
              className="cbm-input w-full pl-9"
              autoComplete="off"
            />
          </div>
          {open && (results.length > 0 || searching) && (
            <div
              data-testid={`${testIdPrefix}-results`}
              className="absolute left-0 right-0 mt-1.5 rounded-lg overflow-hidden z-10 max-h-64 overflow-y-auto"
              style={{
                background: "#1A2E5C",
                border: "1px solid rgba(184,196,224,0.16)",
                boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
              }}
            >
              {searching ? (
                <div className="px-3 py-3 text-cbm-muted text-xs">Recherche…</div>
              ) : (
                results.map((item) => (
                  <button
                    type="button"
                    key={item.user_id}
                    data-testid={`${testIdPrefix}-result-${item.user_id}`}
                    onClick={() => pick(item)}
                    className="w-full text-left px-3 py-2.5 hover:bg-white/5 transition-colors border-b last:border-b-0 cbm-divider"
                  >
                    <p className="text-white text-sm font-medium">{item.nom_prenom}</p>
                    <p className="text-cbm-muted text-xs">
                      {item.entreprise || "—"}{item.email ? ` · ${item.email}` : ""}
                    </p>
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function CreateIntroductionModal({ open, onClose, onCreated }) {
  const [memberA, setMemberA] = useState(null);
  const [memberB, setMemberB] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setMemberA(null);
      setMemberB(null);
      setMessage("");
      setError("");
    }
  }, [open]);

  if (!open) return null;

  const messageLen = message.trim().length;
  const canSubmit = memberA && memberB && messageLen >= MIN_MESSAGE && !loading;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!memberA || !memberB) {
      setError("Veuillez sélectionner les deux membres.");
      return;
    }
    if (memberA.user_id === memberB.user_id) {
      setError("Vous devez sélectionner deux membres différents.");
      return;
    }
    if (messageLen < MIN_MESSAGE) {
      setError(`Le motif doit contenir au moins ${MIN_MESSAGE} caractères.`);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/introductions`,
        {
          user_a_id: memberA.user_id,
          user_b_id: memberB.user_id,
          message: message.trim(),
        },
        { withCredentials: true }
      );
      onCreated?.(data);
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const modal = (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      data-testid="create-introduction-modal"
      style={{ overscrollBehavior: "contain" }}
    >
      <form
        className="cbm-card w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="flex items-start justify-between p-5 border-b cbm-divider">
          <div className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: "rgba(201,162,39,0.15)", border: "1px solid rgba(201,162,39,0.3)" }}
            >
              <Handshake size={16} className="text-gold" />
            </div>
            <div>
              <h2 className="font-outfit font-semibold text-white text-base leading-tight">Créer une recommandation</h2>
              <p className="text-cbm-muted text-xs mt-0.5">Mise en relation entre deux membres</p>
            </div>
          </div>
          <button
            type="button"
            data-testid="close-create-introduction"
            onClick={onClose}
            className="text-cbm-muted hover:text-white p-1 transition-colors"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <MemberPicker
            label="Membre A"
            selected={memberA}
            onSelect={setMemberA}
            exclude={memberB?.user_id}
            testIdPrefix="member-a"
          />
          <MemberPicker
            label="Membre B"
            selected={memberB}
            onSelect={setMemberB}
            exclude={memberA?.user_id}
            testIdPrefix="member-b"
          />

          <div>
            <label className="cbm-label block mb-1.5">Motif de la mise en relation</label>
            <textarea
              data-testid="introduction-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Pourquoi ces deux membres devraient échanger ? Soyez précis : contexte, opportunité, complémentarité…"
              className="cbm-input w-full"
              rows={4}
              maxLength={1000}
            />
            <p className={`text-xs mt-1.5 ${messageLen >= MIN_MESSAGE ? "text-cbm-muted" : "text-amber-300"}`}>
              {messageLen}/{MIN_MESSAGE} caractères minimum
            </p>
          </div>

          {error && (
            <div
              data-testid="introduction-error"
              className="px-3 py-2 rounded-lg text-sm text-red-300 flex items-start gap-2"
              style={{ background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)" }}
            >
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              data-testid="cancel-create-introduction"
              onClick={onClose}
              className="cbm-btn-secondary text-sm flex-1 justify-center py-2.5"
            >
              Annuler
            </button>
            <button
              type="submit"
              data-testid="submit-create-introduction"
              disabled={!canSubmit}
              className="cbm-btn-primary text-sm flex-1 justify-center py-2.5 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Création…" : "Créer la recommandation"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );

  // Render outside the AdminPanel container (which uses overflow-hidden) so
  // the modal is always centered against the viewport, never clipped.
  return createPortal(modal, document.body);
}
